import Phaser from 'phaser';
import { Card } from '../utils/CardUtils';
import { CardSprite } from './CardSprite';
import { CARD_WIDTH, CARD_HEIGHT, GAME_WIDTH, COLORS } from '../utils/Constants';

const SPREAD_SPACING = 25; // pixels between card left edges when spread

export class DiscardPile {
  private scene: Phaser.Scene;
  private x: number;
  private y: number;
  private topSprite: CardSprite | null = null;
  private stackIndicators: Phaser.GameObjects.Rectangle[] = [];
  private countText: Phaser.GameObjects.Text | null = null;
  private spreadBtn: Phaser.GameObjects.Text | null = null;
  private spread = false;
  private spreadSprites: CardSprite[] = [];
  private onDiscardClick?: () => void;
  private cards: Card[] = [];

  constructor(scene: Phaser.Scene, x: number, y: number) {
    this.scene = scene;
    this.x = x;
    this.y = y;
  }

  setOnDiscardClick(callback: () => void): void {
    this.onDiscardClick = callback;
  }

  create(): void {
    this.countText = this.scene.add.text(this.x, this.y + CARD_HEIGHT / 2 + 12, '0', {
      fontSize: '14px',
      color: COLORS.TEXT_LIGHT,
      fontFamily: 'Georgia, serif',
    }).setOrigin(0.5).setDepth(10);

    // Spread toggle button (eye icon)
    this.spreadBtn = this.scene.add.text(this.x + CARD_WIDTH / 2 + 12, this.y - CARD_HEIGHT / 2, '👁', {
      fontSize: '18px',
    }).setOrigin(0.5).setDepth(20)
      .setInteractive({ useHandCursor: true })
      .on('pointerdown', () => this.toggleSpread());
    this.spreadBtn.setVisible(false);
  }

  getPosition(): { x: number; y: number } {
    return { x: this.x, y: this.y };
  }

  updateCards(cards: Card[]): void {
    this.cards = cards;

    // Collapse spread if open
    if (this.spread) this.collapseSpread();

    // Clean up old
    if (this.topSprite) {
      this.topSprite.destroy();
      this.topSprite = null;
    }
    for (const ind of this.stackIndicators) ind.destroy();
    this.stackIndicators = [];

    if (cards.length === 0) {
      // Empty discard area indicator
      const outline = this.scene.add.rectangle(this.x, this.y, CARD_WIDTH, CARD_HEIGHT);
      outline.setStrokeStyle(2, 0x666666);
      outline.setFillStyle(0x000000, 0.1);
      outline.setDepth(1);
      this.stackIndicators.push(outline);

      if (this.countText) this.countText.setText('Discard');
      if (this.spreadBtn) this.spreadBtn.setVisible(false);
      return;
    }

    // Stack height indicators
    const stackCount = Math.min(cards.length, 5);
    for (let i = 0; i < stackCount - 1; i++) {
      const offset = (stackCount - 1 - i) * 2;
      const ind = this.scene.add.rectangle(
        this.x + offset, this.y - offset,
        CARD_WIDTH, CARD_HEIGHT,
        0xcccccc,
      );
      ind.setStrokeStyle(1, 0x999999);
      ind.setDepth(1 + i);
      this.stackIndicators.push(ind);
    }

    // Top card
    const topCard = cards[cards.length - 1];
    this.topSprite = new CardSprite(this.scene, this.x, this.y, topCard, true);
    this.topSprite.setDepth(5 + stackCount);

    this.topSprite.on('pointerdown', () => {
      if (this.onDiscardClick) this.onDiscardClick();
    });

    this.topSprite.on('pointerover', () => {
      this.topSprite?.setTint(0xdddddd);
    });
    this.topSprite.on('pointerout', () => {
      this.topSprite?.clearTint();
    });

    if (this.countText) {
      this.countText.setText(`${cards.length} card${cards.length !== 1 ? 's' : ''}`);
    }

    // Show spread button if more than 1 card
    if (this.spreadBtn) {
      this.spreadBtn.setVisible(cards.length > 1);
    }
  }

  private toggleSpread(): void {
    if (this.spread) {
      this.collapseSpread();
    } else {
      this.expandSpread();
    }
  }

  private expandSpread(): void {
    if (this.spread || this.cards.length === 0) return;
    this.spread = true;

    // Calculate how many cards can fit on screen to the right
    const availableWidth = GAME_WIDTH - this.x - CARD_WIDTH / 2 - 20;
    const maxVisible = Math.floor(availableWidth / SPREAD_SPACING) + 1;
    const startIndex = Math.max(0, this.cards.length - maxVisible);

    for (let i = startIndex; i < this.cards.length; i++) {
      const offsetIndex = i - startIndex;
      const cardX = this.x + offsetIndex * SPREAD_SPACING;
      const sprite = new CardSprite(
        this.scene,
        cardX,
        this.y,
        this.cards[i],
        true,
      );
      sprite.setDepth(50 + i);
      this.spreadSprites.push(sprite);
    }

    // Hide the stacked top card and indicators
    if (this.topSprite) this.topSprite.setVisible(false);
    for (const ind of this.stackIndicators) ind.setVisible(false);

    if (this.spreadBtn) this.spreadBtn.setText('✕');
  }

  private collapseSpread(): void {
    if (!this.spread) return;
    this.spread = false;

    for (const s of this.spreadSprites) s.destroy();
    this.spreadSprites = [];

    // Show the stacked top card and indicators again
    if (this.topSprite) this.topSprite.setVisible(true);
    for (const ind of this.stackIndicators) ind.setVisible(true);

    if (this.spreadBtn) this.spreadBtn.setText('👁');
  }

  isExpanded(): boolean {
    return this.spread;
  }

  getDropZone(): { x: number; y: number; width: number; height: number } {
    return { x: this.x, y: this.y, width: CARD_WIDTH + 30, height: CARD_HEIGHT + 30 };
  }

  destroy(): void {
    this.collapseSpread();
    if (this.topSprite) this.topSprite.destroy();
    for (const ind of this.stackIndicators) ind.destroy();
    if (this.countText) this.countText.destroy();
    if (this.spreadBtn) this.spreadBtn.destroy();
  }
}
